import React, { useState, useEffect } from 'react';
import { ShieldCheck, AlertTriangle, CircleDashed } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useAffiliateClick } from '@/hooks/useAffiliateClick';
import { filterEligibleFirms } from '@/utils/featuredEligibility';
import { getVerificationState, getVerificationLabel, getVerificationColor, formatVerifiedDate, getCtaLabel, hasVerifiedDiscount } from '@/utils/verification';

type PropFirmRow = Database['public']['Tables']['prop_firms']['Row'];
type FundingProgramRow = Database['public']['Tables']['funding_programs']['Row'];

interface FeaturedFirmsProps {
  limit?: number;
}

const BADGE_CONFIG: Record<number, { label: string; color: string }> = {
  1: { label: 'Popular', color: 'bg-emerald-600' },
  2: { label: 'Best Value', color: 'bg-blue-600' },
  3: { label: 'Cheapest', color: 'bg-amber-600' },
};

export const FeaturedFirms = ({ limit }: FeaturedFirmsProps) => {
  const [firms, setFirms] = useState<PropFirmRow[]>([]);
  const [programs, setPrograms] = useState<FundingProgramRow[]>([]);
  const [loading, setLoading] = useState(true);
  const { trackClick } = useAffiliateClick();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch more candidates than needed — eligibility filtering may disqualify some
        const { data: firmData, error: firmError } = await supabase
          .from('prop_firms')
          .select('*')
          .eq('is_active', true)
          .eq('is_featured', true)
          .order('display_order', { ascending: true });

        if (firmError) throw firmError;

        const firmIds = (firmData || []).map(f => f.id);

        // Fetch ALL active programs for eligibility checks
        const { data: allPrograms, error: programError } = await supabase
          .from('funding_programs')
          .select('*')
          .in('prop_firm_id', firmIds)
          .eq('is_active', true);

        if (programError) throw programError;

        // Apply strict eligibility rules — only firms with affiliate links,
        // pricing data, and critical fields pass through
        const eligibleFirms = filterEligibleFirms(
          firmData || [],
          allPrograms || []
        ).slice(0, limit ?? 3);

        // Filter to $50K programs for display
        const displayPrograms = (allPrograms || []).filter(
          p => p.account_size === 50000
        );

        setFirms(eligibleFirms);
        setPrograms(displayPrograms);
      } catch (error) {
        console.error('[FeaturedFirms] Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [limit]);

  if (loading) return <div>Loading...</div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {firms.map((firm, idx) => {
        const program = programs.find(p => p.prop_firm_id === firm.id);
        const basePrice = program?.base_price ?? null;
        const effectivePrice = program?.discounted_price ?? basePrice;
        const hasDiscount = program?.discounted_price != null && basePrice != null && program.discounted_price < basePrice;

        const badge = BADGE_CONFIG[idx + 1] ?? null;
        const targetUrl = firm.affiliate_url || firm.website_url || '#';

        const handleGetStarted = () => {
          trackClick({
            firmName: firm.name,
            accountSize: '50k',
            discountCode: firm.coupon_code || null,
            affiliateUrl: targetUrl,
            source: 'featured_firms',
            skipNavigation: true,
          });
        };

        return (
          <div
            key={firm.id}
            className="relative rounded-2xl border shadow-md p-8 flex flex-col h-full min-w-[290px] transition-transform duration-200 hover:-translate-y-2 hover:shadow-xl bg-white border-gray-100 dark:bg-[#232A36] dark:border-[#232A36]"
          >
            {/* Verification state badge */}
            {(() => {
              const vState = getVerificationState(firm.last_verified_at);
              const vColor = getVerificationColor(vState);
              const Icon = vState === 'verified' ? ShieldCheck : vState === 'stale' ? AlertTriangle : CircleDashed;
              return (
                <span className={`absolute top-6 right-6 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${vColor.bg} ${vColor.text} ${vColor.border} border`}>
                  <Icon className="h-3 w-3" />
                  {getVerificationLabel(vState)}
                </span>
              );
            })()}

            <div className="mb-4">
              <h3 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-1 leading-tight break-words">
                {firm.name}
              </h3>
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-2 min-h-[20px]">
                {firm.notable_features}
              </div>
            </div>

            <div className="mb-2 flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400">Account Size</span>
                <span className="text-lg font-bold text-gray-900 dark:text-white">$50,000</span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">Current Price</span>
                <div className="flex flex-col items-end gap-0.5">
                  {hasDiscount && basePrice != null && (
                    <span className="text-gray-400 dark:text-gray-500 line-through text-sm leading-none">
                      ${basePrice.toFixed(2)}
                    </span>
                  )}
                  <span className={`text-2xl font-bold leading-none ${hasDiscount ? 'text-emerald-500 dark:text-emerald-400' : 'text-[#188FFF]'}`}>
                    {effectivePrice != null ? `$${effectivePrice.toFixed(2)}` : 'N/A'}
                  </span>
                  {hasDiscount && firm.coupon_code && (
                    <span className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded leading-none mt-0.5">
                      {firm.coupon_code}
                    </span>
                  )}
                </div>
              </div>
              {!hasDiscount && firm.discount_description && (
                <span className="text-xs text-green-600 dark:text-green-400 mt-1 text-right block">
                  {firm.discount_description}
                </span>
              )}
            </div>

            {/* Why featured: data-driven explanation */}
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
              Featured: verified affiliate partner · {formatVerifiedDate(firm.last_verified_at)}
            </p>

            <div className="flex-grow" />

            <Button
              variant="cta"
              size="cta"
              className="w-full mt-6 group"
              asChild
            >
              <a
                href={targetUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleGetStarted}
              >
                {getCtaLabel(!!firm.affiliate_url, hasVerifiedDiscount(program?.base_price ?? null, program?.discounted_price ?? null, firm.last_verified_at))}
                <svg className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </a>
            </Button>
          </div>
        );
      })}
    </div>
  );
};
